/**
 * Step 4 — Expected outcomes.
 *
 * Ported from the FAIR app's AI Governance intake. The guidance copy and the "Try:"
 * hint chips are domain content, carried over verbatim.
 */
import React from 'react';
import type { ExpectedOutcome } from '../../../types/models';
import { OUTCOME_HINTS } from '../../../constants/aiGovernance';
import { RepeatableRowCard, newRowId, useRepeatableRows } from '../../../components/RepeatableRows';
import type { StepProps } from './types';

const blankOutcome = (): ExpectedOutcome => ({
  id: newRowId(),
  outcome: '',
  metric: '',
  targetDate: '',
});

const isBlankOutcome = (o: ExpectedOutcome): boolean =>
  !o.outcome.trim() && !o.metric.trim() && !o.targetDate;

const Step4Outcomes: React.FC<StepProps> = ({ useCase, update }) => {
  const { rows, add, canAdd, patch, remove, canRemove } = useRepeatableRows<ExpectedOutcome>({
    stored: useCase.outcomes,
    makeBlank: blankOutcome,
    isBlank: isBlankOutcome,
    onChange: outcomes => update({ outcomes }),
  });

  return (
    <div>
      {/* The shell's card head already names the step, so the source's own heading becomes the
          lead sentence here — matching how steps 1–3 open. */}
      <p className="np-sub mb-4">
        <strong>What will success look like?</strong> Define measurable outcomes.{' '}
        <em>&quot;Reduce processing time from 10 min to 2 min by Q3 2026&quot;</em> is useful.{' '}
        <em>&quot;Improve efficiency&quot;</em> is not.
      </p>

      {rows.map((o, i) => (
        <RepeatableRowCard
          key={o.id}
          label="Outcome"
          index={i}
          onRemove={canRemove ? () => remove(o.id) : undefined}
        >
          <div className="row g-3">
            <div className="col-12">
              <div className="np-field mb-0">
                <label htmlFor={`outcome-what-${o.id}`}>What will be different?</label>
                <input
                  id={`outcome-what-${o.id}`}
                  type="text"
                  className="np-input"
                  placeholder="e.g. Support agents spend less time on repetitive queries"
                  value={o.outcome}
                  onChange={e => patch(o.id, { outcome: e.target.value })}
                />
              </div>
            </div>
            <div className="col-12 col-md-8">
              <div className="np-field mb-0">
                <label htmlFor={`outcome-metric-${o.id}`}>How will you measure this?</label>
                <input
                  id={`outcome-metric-${o.id}`}
                  type="text"
                  className="np-input"
                  placeholder="e.g. Average handle time per ticket in Zendesk"
                  value={o.metric}
                  onChange={e => patch(o.id, { metric: e.target.value })}
                />
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="np-field mb-0">
                <label htmlFor={`outcome-date-${o.id}`}>Target date</label>
                <input
                  id={`outcome-date-${o.id}`}
                  type="date"
                  className="np-input"
                  value={o.targetDate}
                  onChange={e => patch(o.id, { targetDate: e.target.value })}
                />
              </div>
            </div>
          </div>
        </RepeatableRowCard>
      ))}

      <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
        <button
          type="button"
          className="np-btn np-btn-ghost np-btn-sm flex-shrink-0"
          onClick={() => add()}
          disabled={!canAdd}
          title={canAdd ? 'Add another outcome' : 'Complete the outcome above first'}
        >
          <i className="ri-add-line"></i>Add outcome
        </button>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="np-hint m-0">Try:</span>
          <div className="np-pill-toggle-group">
            {OUTCOME_HINTS.slice(0, 3).map(hint => (
              <button
                key={hint}
                type="button"
                className="np-pill-toggle"
                title={hint}
                onClick={() => {
                  // Source behaviour: seed the trailing row whenever its outcome is still
                  // empty — even if a metric has already been typed there — and only start a
                  // new row once that row has an outcome of its own.
                  const last = rows[rows.length - 1];
                  if (!last.outcome.trim()) patch(last.id, { outcome: hint });
                  else add({ outcome: hint });
                }}
              >
                {hint.split(' ').slice(0, 4).join(' ')}…
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step4Outcomes;
